import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import Loading from '../../components/Loading';
import AuthContext from '../../context/AuthContext';
import useIntl from '../../hooks/useIntl';
import api from '../../services/api';
import { AvailableEpisode, Episode, Season, Tv } from '../../types/types';
import WatchedButton from '../WatchedButton';
import EpisodeItem from './EpisodeItem';
interface PropTypes {
  data: {
    tv_id: string;
    tvTitle: string;
    name: string;
    season_number: number;
    available_episodes: AvailableEpisode[];
    is_fully_watched?: boolean;
    number_of_watched_episodes?: number;
  };
  className?: string;
}
function SeasonList({
  data,
  className
}: PropTypes) {
  const auth = React.useContext(AuthContext);
  const intl = useIntl();
  const t = useTranslations();
  const queryClient = useQueryClient();
  const seasonQuery = useQuery<{
    data: Season;
  }>({
    queryKey: [`tv-${data.tv_id}-seasons-${data.season_number}`],
    queryFn: async () => {
      const response = await api.get(`/v1/tv/${data.tv_id}/seasons/${data.season_number}`, {
        params: {
          region: intl.locale
        },
        headers: {
          'accept-language': intl.full_language
        }
      });
      return response.data;
    }
  });
  const {
    mutate: markPreviousEpisodesAsWatched
  } = useMutation({
    mutationFn: async ({
      tvId,
      episodeNumber,
      seasonNumber
    }: {
      tvId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response_0 = await api.post(`/v1/me/mark-previous-episodes-as-watched`, {
        tv_id: tvId,
        season_number: seasonNumber,
        episode_number: episodeNumber
      });
      return response_0.data;
    },
    onMutate: ({
      tvId: tvId_0,
      episodeNumber: episodeNumber_0,
      seasonNumber: seasonNumber_0
    }) => {
      const previousSeason = queryClient.getQueryData([`tv-${tvId_0}-seasons-${seasonNumber_0}`]);
      queryClient.setQueryData<{
        data: Season;
      } | undefined>([`tv-${tvId_0}-seasons-${seasonNumber_0}`], old => {
        if (old) {
          return {
            data: {
              ...old.data,
              episodes: old.data.episodes.map(episode => {
                if (episode.episode_number <= episodeNumber_0) {
                  if (episode.released) {
                    return {
                      ...episode,
                      user_related: {
                        ...episode.user_related,
                        is_watched: true,
                        watch_count: 1
                      }
                    };
                  }
                  return episode;
                }
                return episode;
              })
            }
          };
        }
      });
      for (let currentSeasonNumber = seasonNumber_0 - 1; seasonNumber_0 > 0; seasonNumber_0--) {
        queryClient.setQueryData<{
          data: Season;
        } | undefined>([`tv-${tvId_0}-seasons-${currentSeasonNumber}`], old_0 => {
          if (old_0) {
            return {
              data: {
                ...old_0.data,
                episodes: old_0.data.episodes.map(episode_0 => {
                  if (episode_0.released) {
                    return {
                      ...episode_0,
                      user_related: {
                        ...episode_0.user_related,
                        is_watched: true
                      }
                    };
                  }
                  return episode_0;
                })
              }
            };
          }
        });
      }
      queryClient.setQueryData<{
        data: Tv;
      } | undefined>([`tv-${tvId_0}`], old_1 => {
        if (old_1) {
          const isFullyWatched = old_1.data.available_episodes[old_1.data.available_episodes.length - 1].episode_number === episodeNumber_0 && old_1.data.available_episodes[old_1.data.available_episodes.length - 1].season_number === seasonNumber_0;
          return {
            data: {
              ...old_1.data,
              user_related: {
                ...old_1.data.user_related,
                is_fully_watched: isFullyWatched,
                number_of_watched_episodes: old_1.data.available_episodes.length
              }
            }
          };
        }
      });
      return () => queryClient.setQueryData([`tv-${tvId_0}-seasons-${seasonNumber_0}`], previousSeason);
    },
    onSuccess: (_, {
      tvId: tvId_1
    }) => {
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_1}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-all']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-watching']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-not-started']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-stopped-watching']
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
    },
    onError: (err, __0, rollback: any) => rollback()
  });
  const {
    mutate: markEpisodeAsWatched
  } = useMutation({
    mutationFn: async ({
      tvId: tvId_2,
      episodeNumber: episodeNumber_1,
      seasonNumber: seasonNumber_1
    }: {
      tvId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response_1 = await api.post(`/v1/me/mark-episode-as-watched`, {
        tv_id: tvId_2,
        season_number: seasonNumber_1,
        episode_number: episodeNumber_1
      });
      return response_1.data;
    },
    onMutate: ({
      tvId: tvId_3,
      episodeNumber: episodeNumber_2,
      seasonNumber: seasonNumber_2
    }) => {
      const previousSeason_0 = queryClient.getQueryData([`tv-${tvId_3}-seasons-${seasonNumber_2}`]) as Season;
      queryClient.setQueryData<{
        data: Tv;
      } | undefined>([`tv-${tvId_3}`], old_2 => {
        if (old_2) {
          const availableReleasedEpisodes = old_2.data.available_episodes.filter(availableEpisode => availableEpisode.is_released);
          return {
            data: {
              ...old_2.data,
              user_related: {
                ...old_2.data.user_related,
                is_fully_watched: old_2.data.user_related.number_of_watched_episodes + 1 === availableReleasedEpisodes.length,
                number_of_watched_episodes: old_2.data.user_related.number_of_watched_episodes + 1
              },
              seasons: old_2.data.seasons.map(season => {
                const numberOfSeasonWatchedEpisodes = season.user_related.number_of_watched_episodes;
                if (season.season_number === seasonNumber_2) {
                  return {
                    ...season,
                    user_related: {
                      ...season.user_related,
                      is_fully_watched: availableReleasedEpisodes.filter(availableEpisode_0 => availableEpisode_0.season_number === seasonNumber_2).length === numberOfSeasonWatchedEpisodes + 1,
                      number_of_watched_episodes: numberOfSeasonWatchedEpisodes + 1
                    }
                  };
                }
                return season;
              })
            }
          };
        }
      });
      queryClient.setQueryData<{
        data: Season;
      } | undefined>([`tv-${tvId_3}-seasons-${seasonNumber_2}`], old_3 => {
        if (old_3) {
          const numberOfSeasonWatchedEpisodes_0 = old_3.data.user_related.number_of_watched_episodes;
          return {
            data: {
              ...old_3.data,
              episodes: old_3.data.episodes.map(episode_1 => {
                if (episode_1.episode_number === episodeNumber_2) {
                  return {
                    ...episode_1,
                    user_related: {
                      ...episode_1.user_related,
                      is_watched: true,
                      watch_count: 1
                    }
                  };
                }
                return episode_1;
              }),
              user_related: {
                ...old_3.data.user_related,
                is_fully_watched: old_3.data.episodes.length === numberOfSeasonWatchedEpisodes_0 + 1,
                number_of_watched_episodes: numberOfSeasonWatchedEpisodes_0 + 1
              }
            }
          };
        }
      });
      return () => queryClient.setQueryData([`tv-${tvId_3}-seasons-${seasonNumber_2}`], previousSeason_0);
    },
    onSuccess: (__1, {
      tvId: tvId_4,
      seasonNumber: seasonNumber_3
    }) => {
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_4}-seasons-${seasonNumber_3}`]
      });
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_4}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-all']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-watching']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-not-started']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-stopped-watching']
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['me-detailed-statistics']
      });
      queryClient.invalidateQueries({
        queryKey: [`user-${auth.userData?._id}-watch-list-tvs-with-pagination`]
      });
    },
    onError: (err_0, __2, rollback_0: any) => rollback_0()
  });
  const {
    mutate: unmarkEpisodeAsWatched
  } = useMutation({
    mutationFn: async ({
      tvId: tvId_5,
      episodeNumber: episodeNumber_3,
      seasonNumber: seasonNumber_4
    }: {
      tvId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response_2 = await api.post(`/v1/me/unmark-episode-as-watched`, {
        tv_id: tvId_5,
        season_number: seasonNumber_4,
        episode_number: episodeNumber_3
      });
      return response_2.data;
    },
    onMutate: ({
      tvId: tvId_6,
      episodeNumber: episodeNumber_4,
      seasonNumber: seasonNumber_5
    }) => {
      const previousSeason_1 = queryClient.getQueryData([`tv-${tvId_6}-seasons-${seasonNumber_5}`]) as Season;
      queryClient.setQueryData<{
        data: Tv;
      } | undefined>([`tv-${tvId_6}`], old_4 => {
        if (old_4) {
          const availableReleasedEpisodes_0 = old_4.data.available_episodes.filter(availableEpisode_1 => availableEpisode_1.is_released);
          return {
            data: {
              ...old_4?.data,
              user_related: {
                ...old_4?.data.user_related,
                is_fully_watched: old_4.data.user_related.number_of_watched_episodes - 1 === availableReleasedEpisodes_0.length,
                number_of_watched_episodes: old_4.data.user_related.number_of_watched_episodes - 1
              },
              seasons: old_4.data.seasons.map(season_0 => {
                const numberOfSeasonWatchedEpisodes_1 = season_0.user_related.number_of_watched_episodes;
                if (season_0.season_number === seasonNumber_5) {
                  return {
                    ...season_0,
                    user_related: {
                      ...season_0.user_related,
                      is_fully_watched: availableReleasedEpisodes_0.filter(availableEpisode_2 => availableEpisode_2.season_number === seasonNumber_5).length === numberOfSeasonWatchedEpisodes_1 - 1,
                      number_of_watched_episodes: numberOfSeasonWatchedEpisodes_1 - 1
                    }
                  };
                }
                return season_0;
              })
            }
          };
        }
      });
      queryClient.setQueryData<{
        data: Season;
      } | undefined>([`tv-${tvId_6}-seasons-${seasonNumber_5}`], old_5 => {
        if (old_5) {
          const numberOfSeasonWatchedEpisodes_2 = old_5.data.user_related.number_of_watched_episodes;
          return {
            data: {
              ...old_5.data,
              episodes: old_5.data.episodes.map((episode_2: Episode) => {
                if (episode_2.episode_number === episodeNumber_4) {
                  return {
                    ...episode_2,
                    user_related: {
                      ...episode_2.user_related,
                      is_watched: false
                    }
                  };
                }
                return episode_2;
              }),
              user_related: {
                ...old_5.data.user_related,
                is_fully_watched: old_5.data.episodes.length === numberOfSeasonWatchedEpisodes_2 - 1,
                number_of_watched_episodes: numberOfSeasonWatchedEpisodes_2 - 1
              }
            }
          };
        }
      });
      return () => queryClient.setQueryData([`tv-${tvId_6}-seasons-${seasonNumber_5}`], previousSeason_1);
    },
    onSuccess: (__3, {
      tvId: tvId_7
    }) => {
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_7}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-all']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-watching']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-not-started']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-stopped-watching']
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['me-detailed-statistics']
      });
      queryClient.invalidateQueries({
        queryKey: [`user-${auth.userData?._id}-watch-list-tvs-with-pagination`]
      });
    },
    onError: (err_1, __4, rollback_1: any) => rollback_1()
  });
  const {
    mutate: markEpisodeAsRewatched
  } = useMutation({
    mutationFn: async ({
      tvId: tvId_8,
      episodeNumber: episodeNumber_5,
      seasonNumber: seasonNumber_6
    }: {
      tvId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response_3 = await api.post(`/v1/me/mark-episode-as-watched`, {
        tv_id: tvId_8,
        season_number: seasonNumber_6,
        episode_number: episodeNumber_5,
        is_rewatch: true
      });
      return response_3.data;
    },
    onMutate: ({
      tvId: tvId_9,
      seasonNumber: seasonNumber_7,
      episodeNumber: episodeNumber_6
    }) => {
      queryClient.setQueryData<{
        data: Season;
      } | undefined>([`tv-${tvId_9}-seasons-${seasonNumber_7}`], old_6 => {
        if (old_6) {
          return {
            data: {
              ...old_6.data,
              episodes: old_6.data.episodes.map((episode_3: Episode) => {
                if (episode_3.episode_number === episodeNumber_6) {
                  return {
                    ...episode_3,
                    user_related: {
                      ...episode_3.user_related,
                      watch_count: episode_3.user_related.watch_count + 1
                    }
                  };
                }
                return episode_3;
              })
            }
          };
        }
      });
    },
    onSuccess: (__5, {
      tvId: tvId_10,
      seasonNumber: seasonNumber_8
    }) => {
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_10}-seasons-${seasonNumber_8}`]
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['me-detailed-statistics']
      });
    }
  });
  const {
    mutate: resetEpisodeWatches
  } = useMutation({
    mutationFn: async ({
      tvId: tvId_11,
      episodeNumber: episodeNumber_7,
      seasonNumber: seasonNumber_9
    }: {
      tvId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response_4 = await api.post(`/v1/me/unmark-episode-as-watched`, {
        tv_id: tvId_11,
        season_number: seasonNumber_9,
        episode_number: episodeNumber_7,
        reset_watches: true
      });
      return response_4.data;
    },
    onMutate: ({
      tvId: tvId_12,
      episodeNumber: episodeNumber_8,
      seasonNumber: seasonNumber_10
    }) => {
      const previousSeason_2 = queryClient.getQueryData([`tv-${tvId_12}-seasons-${seasonNumber_10}`]) as Season;
      queryClient.setQueryData<{
        data: Tv;
      } | undefined>([`tv-${tvId_12}`], old_7 => {
        if (old_7) {
          const availableReleasedEpisodes_1 = old_7.data.available_episodes.filter(availableEpisode_3 => availableEpisode_3.is_released);
          return {
            data: {
              ...old_7?.data,
              user_related: {
                ...old_7?.data.user_related,
                is_fully_watched: old_7.data.user_related.number_of_watched_episodes - 1 === availableReleasedEpisodes_1.length,
                number_of_watched_episodes: old_7.data.user_related.number_of_watched_episodes - 1
              },
              seasons: old_7.data.seasons.map(season_1 => {
                const numberOfSeasonWatchedEpisodes_3 = season_1.user_related.number_of_watched_episodes;
                if (season_1.season_number === seasonNumber_10) {
                  return {
                    ...season_1,
                    user_related: {
                      ...season_1.user_related,
                      is_fully_watched: availableReleasedEpisodes_1.filter(availableEpisode_4 => availableEpisode_4.season_number === seasonNumber_10).length === numberOfSeasonWatchedEpisodes_3 - 1,
                      number_of_watched_episodes: numberOfSeasonWatchedEpisodes_3 - 1
                    }
                  };
                }
                return season_1;
              })
            }
          };
        }
      });
      queryClient.setQueryData<{
        data: Season;
      } | undefined>([`tv-${tvId_12}-seasons-${seasonNumber_10}`], old_8 => {
        if (old_8) {
          const numberOfSeasonWatchedEpisodes_4 = old_8.data.user_related.number_of_watched_episodes;
          return {
            data: {
              ...old_8.data,
              episodes: old_8.data.episodes.map((episode_4: Episode) => {
                if (episode_4.episode_number === episodeNumber_8) {
                  return {
                    ...episode_4,
                    user_related: {
                      ...episode_4.user_related,
                      is_watched: false
                    }
                  };
                }
                return episode_4;
              }),
              user_related: {
                ...old_8.data.user_related,
                is_fully_watched: old_8.data.episodes.length === numberOfSeasonWatchedEpisodes_4 - 1,
                number_of_watched_episodes: numberOfSeasonWatchedEpisodes_4 - 1
              }
            }
          };
        }
      });
      return () => queryClient.setQueryData([`tv-${tvId_12}-seasons-${seasonNumber_10}`], previousSeason_2);
    },
    onSuccess: (__6, {
      tvId: tvId_13,
      seasonNumber: seasonNumber_11
    }) => {
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_13}-seasons-${seasonNumber_11}`]
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['me-detailed-statistics']
      });
      queryClient.invalidateQueries({
        queryKey: [`user-${auth.userData?._id}-watch-list-tvs-with-pagination`]
      });
    },
    onError: (err_2, __7, rollback_2: any) => rollback_2()
  });
  const {
    mutate: resetEpisodeRewatches
  } = useMutation({
    mutationFn: async ({
      tvId: tvId_14,
      episodeNumber: episodeNumber_9,
      seasonNumber: seasonNumber_12
    }: {
      tvId: string;
      episodeNumber: number;
      seasonNumber: number;
    }) => {
      const response_5 = await api.post(`/v1/me/unmark-episode-as-watched`, {
        tv_id: tvId_14,
        season_number: seasonNumber_12,
        episode_number: episodeNumber_9,
        reset_rewatches: true
      });
      return response_5.data;
    },
    onMutate: ({
      tvId: tvId_15,
      seasonNumber: seasonNumber_13,
      episodeNumber: episodeNumber_10
    }) => {
      queryClient.setQueryData<{
        data: Season;
      } | undefined>([`tv-${tvId_15}-seasons-${seasonNumber_13}`], old_9 => {
        if (old_9) {
          return {
            data: {
              ...old_9.data,
              episodes: old_9.data.episodes.map((episode_5: Episode) => {
                if (episode_5.episode_number === episodeNumber_10) {
                  return {
                    ...episode_5,
                    user_related: {
                      ...episode_5.user_related,
                      watch_count: 1
                    }
                  };
                }
                return episode_5;
              })
            }
          };
        }
      });
    },
    onSuccess: (__8, {
      tvId: tvId_16,
      seasonNumber: seasonNumber_14
    }) => {
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_16}-seasons-${seasonNumber_14}`]
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['me-detailed-statistics']
      });
    }
  });
  const {
    mutate: markSeasonAsWatched
  } = useMutation({
    mutationFn: async ({
      tvId: tvId_17,
      seasonNumber: seasonNumber_15
    }: {
      tvId: string;
      seasonNumber: number;
    }) => {
      const response_6 = await api.post(`/v1/me/mark-season-as-watched`, {
        tv_id: tvId_17,
        season_number: seasonNumber_15
      });
      return response_6.data;
    },
    onMutate: ({
      tvId: tvId_18,
      seasonNumber: seasonNumber_16
    }) => {
      const previousSeason_3 = queryClient.getQueryData([`tv-${tvId_18}-seasons-${seasonNumber_16}`]);
      queryClient.setQueryData<{
        data: Tv;
      } | undefined>([`tv-${tvId_18}`], old_10 => {
        if (old_10) {
          return {
            data: {
              ...old_10.data,
              seasons: old_10.data.seasons.map(season_2 => {
                if (season_2.season_number === seasonNumber_16) {
                  return {
                    ...season_2,
                    user_related: {
                      ...season_2.user_related,
                      is_fully_watched: true
                    }
                  };
                }
                return season_2;
              })
            }
          };
        }
      });
      queryClient.setQueryData<{
        data: Season;
      } | undefined>([`tv-${tvId_18}-seasons-${seasonNumber_16}`], old_11 => {
        if (old_11) {
          return {
            data: {
              ...old_11.data,
              user_related: {
                ...old_11.data.user_related,
                number_of_watched_episodes: old_11.data.episodes.length
              },
              episodes: old_11.data.episodes.map(episode_6 => {
                if (episode_6.user_related.watch_count < 1) {
                  return {
                    ...episode_6,
                    user_related: {
                      ...episode_6.user_related,
                      is_watched: true,
                      watch_count: 1
                    }
                  };
                }
                return episode_6;
              })
            }
          };
        }
      });
      return () => queryClient.setQueryData([`tv-${tvId_18}-seasons-${seasonNumber_16}`], previousSeason_3);
    },
    onSuccess: (__9, {
      tvId: tvId_19
    }) => {
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_19}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-all']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-watching']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-not-started']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-stopped-watching']
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['me-detailed-statistics']
      });
      queryClient.invalidateQueries({
        queryKey: [`user-${auth.userData?._id}-watch-list-tvs-with-pagination`]
      });
    },
    onError: (err_3, __10, rollback_3: any) => rollback_3()
  });
  const {
    mutate: unmarkSeasonAsWatched
  } = useMutation({
    mutationFn: async ({
      tvId: tvId_20,
      seasonNumber: seasonNumber_17
    }: {
      tvId: string;
      seasonNumber: number;
    }) => {
      const response_7 = await api.post(`/v1/me/unmark-season-as-watched`, {
        tv_id: tvId_20,
        season_number: seasonNumber_17
      });
      return response_7.data;
    },
    onMutate: ({
      tvId: tvId_21,
      seasonNumber: seasonNumber_18
    }) => {
      const previousSeason_4 = queryClient.getQueryData([`tv-${tvId_21}-seasons-${seasonNumber_18}`]);
      queryClient.setQueryData<{
        data: Tv;
      } | undefined>([`tv-${tvId_21}`], old_12 => {
        if (old_12) {
          return {
            data: {
              ...old_12.data,
              seasons: old_12.data.seasons.map(season_3 => {
                if (season_3.season_number === seasonNumber_18) {
                  return {
                    ...season_3,
                    user_related: {
                      ...season_3.user_related,
                      is_fully_watched: false
                    }
                  };
                }
                return season_3;
              })
            }
          };
        }
      });
      queryClient.setQueryData<{
        data: Season;
      } | undefined>([`tv-${tvId_21}-seasons-${seasonNumber_18}`], old_13 => {
        if (old_13) {
          return {
            data: {
              ...old_13.data,
              episodes: old_13.data.episodes.map(episode_7 => {
                return {
                  ...episode_7,
                  user_related: {
                    ...episode_7.user_related,
                    is_watched: false
                  }
                };
              })
            }
          };
        }
      });
      return () => queryClient.setQueryData([`tv-${tvId_21}-seasons-${seasonNumber_18}`], previousSeason_4);
    },
    onSuccess: (__11, {
      tvId: tvId_22
    }) => {
      queryClient.invalidateQueries({
        queryKey: [`tv-${tvId_22}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-all']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-watching']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-not-started']
      });
      queryClient.invalidateQueries({
        queryKey: ['user-tv-to-watch-next-stopped-watching']
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      queryClient.invalidateQueries({
        queryKey: ['me-detailed-statistics']
      });
      queryClient.invalidateQueries({
        queryKey: [`user-${auth.userData?._id}-watch-list-tvs-with-pagination`]
      });
    },
    onError: (err_4, __12, rollback_4: any) => rollback_4()
  });
  function handleMarkPreviousEpisodesAsWatched(episodeNumber_11: number) {
    markPreviousEpisodesAsWatched({
      tvId: data.tv_id,
      seasonNumber: data.season_number,
      episodeNumber: episodeNumber_11
    });
  }
  function handleMarkEpisodeAsWatched(episodeNumber_12: number) {
    markEpisodeAsWatched({
      tvId: data.tv_id,
      episodeNumber: episodeNumber_12,
      seasonNumber: data.season_number
    });
  }
  function handleUnmarkEpisodeAsWatched(episodeNumber_13: number) {
    unmarkEpisodeAsWatched({
      tvId: data.tv_id,
      episodeNumber: episodeNumber_13,
      seasonNumber: data.season_number
    });
  }
  function handleMarkSeasonAsWatched(seasonNumber_19: number) {
    markSeasonAsWatched({
      tvId: data.tv_id,
      seasonNumber: seasonNumber_19
    });
  }
  function handleUnmarkSeasonAsWatched(seasonNumber_20: number) {
    unmarkSeasonAsWatched({
      tvId: data.tv_id,
      seasonNumber: seasonNumber_20
    });
  }
  if (seasonQuery.isLoading) {
    return <div className="mt-4">
        <Loading color={'#fff'} size={20} />
      </div>;
  }
  return <div className={`flex flex-col justify-center ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 justify-between items-start mt-3">
        {seasonQuery.data && <div className="w-full md:w-[70%]">
            {seasonQuery.data.data.air_date && <span className="text-white font-bold">
                {t('tv_show.episodes.release_year', {
            release_year: dayjs(seasonQuery.data.data.air_date).format('YYYY')
          })}
              </span>}
            <p className="text-[#a3a3a3] line-clamp-4">
              {seasonQuery.data?.data.overview || t('tv_show.episodes.no_overview_available')}
            </p>
          </div>}

        {auth.isLoggedIn && <div className="flex flex-col items-start md:items-end mt-4 md:mt-0">
            <span className="text-white font-bold mb-2">
              {t('tv_show.episodes.you_watched_n_of_episodes', {
            watched_episodes: String(data.number_of_watched_episodes),
            total_episodes: data.available_episodes.filter(availableEpisode_5 => availableEpisode_5.season_number === data.season_number).length
          })}
            </span>

            <WatchedButton btnType="text" isSmall={true} isWatched={!!data.is_fully_watched} disabled={!(data.available_episodes.filter(availableEpisode_6 => availableEpisode_6.season_number === data.season_number && availableEpisode_6.is_released).length > 0)} onClick={() => {
          data.is_fully_watched ? handleUnmarkSeasonAsWatched(data.season_number) : handleMarkSeasonAsWatched(data.season_number);
        }} />
          </div>}
      </div>

      <div className="h-[1px] bg-gray-200 my-4" />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-4">
        {seasonQuery.isSuccess && seasonQuery.data?.data.episodes.map((episode_8, index) => {
        const previousEpisode = seasonQuery.data?.data.episodes[index - 1];
        let isPreviousEpisodeWatched = 'not_watched';
        if (auth.isLoggedIn) {
          if (previousEpisode) {
            isPreviousEpisodeWatched = previousEpisode?.user_related.is_watched ? 'is_watched' : 'not_watched';
          } else {
            isPreviousEpisodeWatched = 'is_watched';
          }
        }
        return <EpisodeItem className="mb-2" key={episode_8.episode_number} data={{
          _id: episode_8._id,
          tv: {
            _id: data.tv_id,
            title: data.tvTitle
          },
          name: episode_8.name,
          overview: episode_8.overview,
          season_number: data.season_number,
          episode_number: episode_8.episode_number,
          wallpaper: episode_8.wallpaper,
          air_date: episode_8.air_date,
          released: episode_8.released,
          average_rating: episode_8.average_rating,
          user_related: episode_8.user_related,
          credits: episode_8.credits,
          watch_providers: episode_8.watch_providers
        }} isPreviousEpisodeWatched={isPreviousEpisodeWatched} onMarkPreviousEpisodesAsWatched={handleMarkPreviousEpisodesAsWatched} onMarkAsWatched={handleMarkEpisodeAsWatched} onUnmarkAsWatched={handleUnmarkEpisodeAsWatched} onRewatch={() => {
          markEpisodeAsRewatched({
            tvId: data.tv_id,
            seasonNumber: data.season_number,
            episodeNumber: episode_8.episode_number
          });
        }} onResetWatches={() => {
          resetEpisodeWatches({
            tvId: data.tv_id,
            seasonNumber: data.season_number,
            episodeNumber: episode_8.episode_number
          });
        }} onResetRewatches={() => {
          resetEpisodeRewatches({
            tvId: data.tv_id,
            seasonNumber: data.season_number,
            episodeNumber: episode_8.episode_number
          });
        }} />;
      })}
      </div>
    </div>;
}
export default SeasonList;